import { Text } from '@chakra-ui/layout';
import { NumberInput, NumberInputField } from '@chakra-ui/react';
import { IconMinusSmall } from 'central-icons/IconMinusSmall';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import isNumber from 'lodash/isNumber';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from '@revelio/core';
import { IconButton, Select } from '@revelio/ui';

import { TOP_COMPANIES_MAX } from '../DataProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import {
  type DimensionSource,
  type PlotFilter,
  PlotFilterType,
  type PositionedItem,
} from '../PageConfigProvider/types/item';
import layoutStyles from './data-filter-selector.module.css';
import labelStyles from './section-label.module.css';

type PlotFilterOption = {
  value: PlotFilterType.TOP_N | PlotFilterType.BOTTOM_N;
  label: string;
};

const plotFilterOptions: PlotFilterOption[] = [
  { value: PlotFilterType.TOP_N, label: 'Top' },
  { value: PlotFilterType.BOTTOM_N, label: 'Bottom' },
];

const TOP_N_EDITOR_MIN = 1;
const TOP_N_EDITOR_MAX = 20;

const TOP_N_DEBOUNCE_MS = 350;

const clampTopBottomLimit = (
  raw: number,
  maximum = TOP_N_EDITOR_MAX
): number => {
  if (!isNumber(raw) || Number.isNaN(raw)) return TOP_N_EDITOR_MIN;
  if (raw < TOP_N_EDITOR_MIN) return TOP_N_EDITOR_MIN;
  if (raw > maximum) return maximum;
  return raw;
};

type DebouncedTopBottomNInputProps = {
  filter: PlotFilter;
  plotFilterIndex: number;
  chartItemId: string;
  maximum: number;
  onCommit: (value: number) => void;
};

const DebouncedTopBottomNInput = ({
  filter,
  plotFilterIndex,
  chartItemId,
  maximum,
  onCommit,
}: DebouncedTopBottomNInputProps) => {
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  });

  const [text, setText] = useState(() =>
    String(clampTopBottomLimit(Number(filter.value), maximum))
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setText(String(clampTopBottomLimit(Number(filter.value), maximum)));
  }, [chartItemId, plotFilterIndex, filter.value, maximum]);

  const debouncedText = useDebounce(text, TOP_N_DEBOUNCE_MS);

  useEffect(() => {
    const trimmed = debouncedText.trim();
    if (trimmed === '') return;
    const raw = Number(trimmed);
    if (!isNumber(raw) || Number.isNaN(raw)) return;
    const clamped = clampTopBottomLimit(raw, maximum);
    if (clamped !== Number(filter.value)) {
      onCommitRef.current(clamped);
    }
  }, [debouncedText, filter.value, maximum]);

  return (
    <NumberInput
      size="xs"
      value={text}
      min={TOP_N_EDITOR_MIN}
      max={maximum}
      onChange={(valueString) => setText(valueString)}
      onBlur={() => {
        const trimmed = text.trim();
        if (trimmed === '') return;
        const raw = Number(trimmed);
        if (!isNumber(raw) || Number.isNaN(raw)) return;
        const clamped = clampTopBottomLimit(raw, maximum);
        if (clamped !== Number(filter.value)) {
          onCommitRef.current(clamped);
        }
      }}
      clampValueOnBlur={false}
      maxWidth="60px"
    >
      <NumberInputField />
    </NumberInput>
  );
};

export const DataFilterSelector = ({
  item,
  plotFilterMenu = 'all',
}: {
  item: PositionedItem;
  plotFilterMenu?: 'all' | 'top-only';
}) => {
  const { dispatch } = useTabContentConfig();
  const autoAppliedDimensionRef = useRef<DimensionSource | null>(null);
  const { plotFilters } = item.plot ?? {};
  const isTopCompanies = item.plot?.dimensionSource === 'company';
  const maximum = isTopCompanies ? TOP_COMPANIES_MAX : TOP_N_EDITOR_MAX;
  const isTopOnly = plotFilterMenu === 'top-only' || isTopCompanies;

  const handleAddFilter = () => {
    dispatch({
      type: 'ADD_PLOT_FILTER',
      payload: {
        id: item.id,
        filter: {
          type: PlotFilterType.TOP_N,
          value: clampTopBottomLimit(10, maximum),
        },
      },
    });
  };

  const handleRemoveFilter = (index: number) => {
    dispatch({
      type: 'REMOVE_PLOT_FILTER',
      payload: { plotId: item.id, filterIndex: index },
    });
  };

  const handleChangeFilter = (filter: PlotFilter, index: number) => {
    dispatch({
      type: 'UPDATE_PLOT_FILTER',
      payload: { plotId: item.id, filterIndex: index, filter },
    });
  };

  const topBottomFiltersWithIndices = useMemo(() => {
    if (!plotFilters?.length) return [];

    return plotFilters.reduce<
      Array<{ filter: PlotFilter; plotFilterIndex: number }>
    >((acc, filter, plotFilterIndex) => {
      if (
        filter.type === PlotFilterType.TOP_N ||
        filter.type === PlotFilterType.BOTTOM_N
      ) {
        acc.push({ filter, plotFilterIndex });
      }
      return acc;
    }, []);
  }, [plotFilters]);

  useEffect(() => {
    const dimensionSource = item?.plot?.dimensionSource;
    const shouldDefaultTopTen =
      dimensionSource === 'skills' ||
      dimensionSource === 'role' ||
      dimensionSource === 'geography';
    if (!shouldDefaultTopTen) {
      autoAppliedDimensionRef.current = null;
      return;
    }
    if (autoAppliedDimensionRef.current === dimensionSource) return;
    if (topBottomFiltersWithIndices.length > 0) return;

    dispatch({
      type: 'ADD_PLOT_FILTER',
      payload: {
        id: item.id,
        filter: {
          type: PlotFilterType.TOP_N,
          value: clampTopBottomLimit(10, maximum),
        },
      },
    });
    autoAppliedDimensionRef.current = dimensionSource;
  }, [
    dispatch,
    item.id,
    item?.plot?.dimensionSource,
    maximum,
    topBottomFiltersWithIndices.length,
  ]);

  useEffect(() => {
    topBottomFiltersWithIndices.forEach(({ filter, plotFilterIndex }) => {
      const raw = Number(filter.value);
      const clamped = clampTopBottomLimit(raw, maximum);
      const type = isTopOnly ? PlotFilterType.TOP_N : filter.type;
      if (raw !== clamped || filter.type !== type) {
        dispatch({
          type: 'UPDATE_PLOT_FILTER',
          payload: {
            plotId: item.id,
            filterIndex: plotFilterIndex,
            filter: { type, value: clamped },
          },
        });
      }
    });
  }, [dispatch, isTopOnly, item.id, maximum, topBottomFiltersWithIndices]);

  return (
    <div className={layoutStyles.section}>
      <div className={layoutStyles.sectionHeader}>
        <Text
          className={labelStyles.sectionLabel}
          fontSize="sm"
          fontWeight={600}
          marginBottom="0.5rem"
        >
          Filter
        </Text>
        <IconButton
          variant="ghost"
          size="sm"
          shape="rounded"
          aria-label="add-filter"
          disabled={
            topBottomFiltersWithIndices &&
            topBottomFiltersWithIndices.length >= 1
          }
          icon={<IconPlusSmall />}
          onClick={handleAddFilter}
        />
      </div>
      {topBottomFiltersWithIndices?.map(({ filter, plotFilterIndex }) => (
        <div
          className={layoutStyles.filterRow}
          key={`${filter.type}-${plotFilterIndex}`}
        >
          <div className={layoutStyles.filterField}>
            {isTopOnly ? (
              <Text fontSize="sm" fontWeight={600} lineHeight="2rem">
                Top
              </Text>
            ) : (
              <Select<PlotFilterOption>
                size="sm"
                isSearchable={false}
                options={plotFilterOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    background:
                      'var(--ccp-rs-control-bg, var(--ui-surface-white))',
                    borderColor:
                      'var(--ccp-rs-border, var(--ui-border-color-normal))',
                  }),
                }}
                value={
                  plotFilterOptions.find(
                    (option) => option.value === filter.type
                  ) ?? null
                }
                onChange={(filterOption) =>
                  filterOption?.value &&
                  handleChangeFilter(
                    {
                      ...filter,
                      type: filterOption.value,
                      value: clampTopBottomLimit(Number(filter.value), maximum),
                    },
                    plotFilterIndex
                  )
                }
              />
            )}
          </div>
          <DebouncedTopBottomNInput
            filter={filter}
            plotFilterIndex={plotFilterIndex}
            chartItemId={item.id}
            maximum={maximum}
            onCommit={(value) =>
              handleChangeFilter({ ...filter, value }, plotFilterIndex)
            }
          />
          <IconButton
            variant="ghost"
            size="sm"
            shape="rounded"
            aria-label="delete-filter"
            icon={<IconMinusSmall />}
            onClick={() => handleRemoveFilter(plotFilterIndex)}
          />
        </div>
      ))}
    </div>
  );
};
